// src/helpers/date.ts

/**
 * Converts a UTC date string to relative and exact local time strings.
 * @param dateString - The UTC date string from the API (e.g., "YYYY-MM-DD HH:MM:SS").
 * @returns An object containing the relative time and the full date string for tooltips.
 */
export const formatSyncTime = (
  dateString: string | null
): { relative: string; exact: string } => {
  if (!dateString) {
    return { relative: "N/A", exact: "N/A" };
  }

  // Ensure the date string is parsed as UTC
  // API format: "YYYY-MM-DD HH:MM:SS"
  const isoDateString = dateString.substring(0, 19).replace(" ", "T") + "Z";
  const date = new Date(isoDateString);

  // 1. Calculate Relative Time
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
  let relative = "just now";

  if (seconds >= 86400) {
    const days = Math.floor(seconds / 86400);
    relative = `${days} day${days > 1 ? "s" : ""} ago`;
  } else if (seconds >= 3600) {
    const hours = Math.floor(seconds / 3600);
    relative = `${hours} hour${hours > 1 ? "s" : ""} ago`;
  } else if (seconds >= 60) {
    const minutes = Math.floor(seconds / 60);
    relative = `${minutes} min${minutes > 1 ? "s" : ""} ago`;
  }

  // 2. Format Exact Time for Tooltip (in user's local timezone)
  const exact = date.toString();

  return { relative, exact };
};

/**
 * Formats a date string as relative time (past or future) plus exact for tooltips.
 * Use for "next sync" or any timestamp that can be in the future.
 */
export const formatSyncTimeWithDirection = (
  dateString: string | null
): { relative: string; exact: string } => {
  if (!dateString) {
    return { relative: "—", exact: "—" };
  }

  const normalized = dateString.substring(0, 19).replace(" ", "T") + (dateString.includes("Z") ? "" : "Z");
  const date = new Date(normalized);
  const now = new Date().getTime();
  const ts = date.getTime();
  const seconds = Math.floor((ts - now) / 1000);
  const absSeconds = Math.abs(seconds);
  let relative = "just now";

  if (absSeconds >= 86400) {
    const days = Math.floor(absSeconds / 86400);
    relative = seconds >= 0 ? `in ${days} day${days > 1 ? "s" : ""}` : `${days} day${days > 1 ? "s" : ""} ago`;
  } else if (absSeconds >= 3600) {
    const hours = Math.floor(absSeconds / 3600);
    relative = seconds >= 0 ? `in ${hours} hour${hours > 1 ? "s" : ""}` : `${hours} hour${hours > 1 ? "s" : ""} ago`;
  } else if (absSeconds >= 60) {
    const minutes = Math.floor(absSeconds / 60);
    relative = seconds >= 0 ? `in ${minutes} min${minutes > 1 ? "s" : ""}` : `${minutes} min${minutes > 1 ? "s" : ""} ago`;
  } else if (seconds > 0) {
    relative = `in ${absSeconds} second${absSeconds === 1 ? "" : "s"}`;
  } else if (seconds < 0) {
    relative = `${absSeconds} second${absSeconds === 1 ? "" : "s"} ago`;
  }

  return { relative, exact: date.toString() };
};
